import {
  CAPTURE_DEVICE_TYPES,
  CaptureDevice,
  Concept,
  EmbedDef,
  InterviewItemTemplate,
  MatrixRow,
  MediaDef,
  MediaDefOrPlaceholder,
  Option,
  OptionId,
  OptionMedia,
  QuestionType,
  RecordingState,
} from "./types";
import { assertNever } from "@/packages/ts-utils";
import {
  getEmbedProxyHost,
  getFigmaEmbedOrigin,
  isEmbedProxyHostname,
  normalizeEmbedDisplayUrl,
  normalizeEmbedProxyUrl,
} from "@/utils/helpers";
import { parsePrivateSurveyAssetUrl } from "@/utils/privateSurveyAssetUrl";
import type { SelectionMode } from "@/utils/questions";
import type { DeviceType } from "@/utils/types";
import { isValidUrl } from "@/utils/validation/url";
import { TFunction } from "i18next";
import { v4 as uuidv4, v5 as uuidv5 } from "uuid";

/**
 * Check if a question type supports user-defined options (choices).
 * Returns true for multiple_choice, scale, and ranking question types.
 */
export const questionTypeHasOptions = (questionType: QuestionType): boolean => {
  return (
    questionType === "multiple_choice" ||
    questionType === "scale" ||
    questionType === "ranking"
  );
};

// Whether a question of this type can be marked skippable. Screener questions
// never can, whatever their type.
export const questionTypeCanBeSkippable = (
  questionType: QuestionType
): boolean => {
  switch (questionType) {
    case "open_ended":
    case "multiple_choice":
    case "scale":
    case "ranking":
    case "matrix":
    case "file_upload":
      return true;
    // Multi-turn items: a skip would strand the remaining screens or rungs.
    // TODO(soon): figure out how skipping should work for these items.
    case "max_diff":
    case "gabor_granger":
    case "conjoint":
      return false;
    default:
      return assertNever(questionType);
  }
};

/**
 * Check if a question type's respondent UI renders an option's image.
 * Narrower than `questionTypeHasOptions`: matrix and scale ignore one.
 */
export const questionTypeRendersOptionImages = (
  questionType: QuestionType
): boolean => {
  return (
    questionType === "multiple_choice" ||
    questionType === "ranking" ||
    questionType === "max_diff"
  );
};

/**
 * Whether a question is in image-option mode.
 *
 * `showOptionImages` post-dates the images themselves, so a question authored
 * before it exists with media and no flag; infer the mode from the media rather
 * than reading it as text-only. An explicit `false` wins, which is how an author
 * turns images off without losing the uploads.
 */
export const questionShowsOptionImages = (
  showOptionImages: boolean | undefined,
  options: readonly { media?: OptionMedia }[] | undefined
): boolean => showOptionImages ?? options?.some((o) => o.media) ?? false;

export const getOptionImageFitClass = (fit: OptionMedia["fit"]) => {
  switch (fit) {
    case undefined:
    case "cover":
      return "object-cover";
    case "contain":
      return "object-contain";
    default:
      return assertNever(fit);
  }
};

// An explicit fit wins; otherwise the same picture as `previous` keeps its setting and a new one starts whole.
export const getFitForOptionImage = (
  image: Pick<OptionMedia, "url" | "fit">,
  previous: OptionMedia | undefined
): OptionMedia["fit"] =>
  image.fit ?? (previous?.url === image.url ? previous.fit : "contain");

/**
 * Check if an interview item has options (choices).
 * Returns true for statements and question types that support options.
 */
export const itemHasOptions = (item: InterviewItemTemplate): boolean => {
  return (
    item.type === "statement" ||
    (item.type === "question" && questionTypeHasOptions(item.questionType))
  );
};

export const areAllDevicesInState = (
  state: string,
  recordingState: RecordingState,
  deviceTypes: CaptureDevice[]
) => {
  return deviceTypes.every((type) => recordingState[type].state === state);
};

export const isAnyDeviceActive = (recordingState: RecordingState) => {
  return CAPTURE_DEVICE_TYPES.some(
    (deviceType) => recordingState[deviceType].state === "active"
  );
};

export const isStandby = (
  recordingState: RecordingState,
  deviceTypes: CaptureDevice[]
) => {
  return (
    recordingState.state === "standby" &&
    (areAllDevicesInState("active", recordingState, deviceTypes) ||
      areAllDevicesInState("inactive", recordingState, deviceTypes))
  );
};

export const isLoading = (
  recordingState: RecordingState,
  deviceTypes: CaptureDevice[]
) => {
  if (recordingState.state === "transcribing") {
    return true;
  }
  return (
    recordingState.state === "standby" &&
    !areAllDevicesInState("active", recordingState, deviceTypes) &&
    !areAllDevicesInState("inactive", recordingState, deviceTypes)
  );
};

export const isRecording = (recordingState: RecordingState) => {
  return recordingState.state === "recording";
};

export const getMediaDefs = ({
  media,
  currentConcept,
}: {
  media?: MediaDefOrPlaceholder[];
  currentConcept?: Concept;
}): MediaDef[] => {
  if (!media) return [];
  return media.flatMap((m) => {
    if (m.type === "concept-placeholder") {
      return (
        // These all live on the placeholder, one toggle per question; concept
        // media slots no longer carry them.
        currentConcept?.content.media?.map((cm): MediaDef => {
          if (cm.type === "video") {
            return {
              ...cm,
              force_watching: m.force_watching,
              show_controls: m.show_controls,
              single_view: m.single_view,
            };
          }
          return { ...cm, view_time_limit_seconds: m.view_time_limit_seconds };
        }) ?? []
      );
    }
    return [m];
  });
};

export const isTranscribing = (recordingState: RecordingState) => {
  return recordingState.state === "transcribing";
};

/**
 * Check if options should be disabled based on recording state.
 * When screen recording, we don't need to be in standby. Otherwise we do.
 * TODO: ENG-333 Can this be removed? Seems like we disable at the interview level now when recording state is
 * not ready.
 */
export const shouldDisableOptions = (
  recordingState: RecordingState | undefined,
  forceDisabled: boolean
): boolean => {
  if (forceDisabled) {
    return true;
  }
  // if screen recording we don't need to be in standby, otherwise we do
  if (
    recordingState?.state !== "standby" &&
    recordingState?.screen.state === "inactive"
  ) {
    return true;
  }
  return false;
};

export const getUpdatedMultiSelectOptionIds = ({
  optionId,
  options,
  selectedOptionIds,
}: {
  optionId: OptionId;
  options: Pick<Option, "id" | "exclusiveOption">[];
  selectedOptionIds: OptionId[];
}): OptionId[] => {
  const selectedOption = options.find((option) => option.id === optionId);
  const isSelected = selectedOptionIds.includes(optionId);

  if (isSelected) {
    return selectedOptionIds.filter((selectedId) => selectedId !== optionId);
  }

  if (selectedOption?.exclusiveOption) {
    return [optionId];
  }

  const exclusiveOptionIds = new Set(
    options
      .filter((option) => option.exclusiveOption)
      .map((option) => option.id)
  );
  return [
    ...selectedOptionIds.filter(
      (selectedId) => !exclusiveOptionIds.has(selectedId)
    ),
    optionId,
  ];
};

export const hasSelectedExclusiveOption = ({
  options,
  selectedOptionIds,
}: {
  options: Pick<Option, "id" | "exclusiveOption">[];
  selectedOptionIds: OptionId[];
}): boolean =>
  options.some(
    (option) => option.exclusiveOption && selectedOptionIds.includes(option.id)
  );

export type SelectionHint =
  | { kind: "select-n"; count: number }
  | { kind: "select-between"; min: number; max: number }
  | { kind: "select-n-more"; count: number }
  | { kind: "select-at-least-n-more"; count: number }
  | { kind: "select-up-to-n-more"; count: number };

export const getSelectionHint = ({
  selectedCount,
  minSelect,
  maxSelect,
  selectionMode,
  hasExclusiveSelection = false,
  hasOtherText = false,
}: {
  selectedCount: number;
  minSelect: number | undefined;
  maxSelect: number | undefined;
  selectionMode: SelectionMode;
  hasExclusiveSelection?: boolean;
  // Other counts toward min remaining only; max uses option ids alone.
  hasOtherText?: boolean;
}): SelectionHint | null => {
  const min = minSelect ?? 1;
  const minCount = selectedCount + (hasOtherText ? 1 : 0);

  if (selectionMode === "unlimited" || selectionMode === "single") return null;
  if (hasExclusiveSelection) return null;

  if (minCount === 0) {
    if (selectionMode === "exact") return { kind: "select-n", count: min };
    if (selectionMode === "range" && maxSelect !== undefined) {
      return { kind: "select-between", min, max: maxSelect };
    }
    return null;
  }

  if (minCount < min) {
    const remaining = min - minCount;
    return selectionMode === "exact"
      ? { kind: "select-n-more", count: remaining }
      : { kind: "select-at-least-n-more", count: remaining };
  }

  // Exact is satisfied once the minimum (including Other) is met.
  if (selectionMode === "exact") return null;

  if (maxSelect !== undefined && selectedCount < maxSelect) {
    return {
      kind: "select-up-to-n-more",
      count: maxSelect - selectedCount,
    };
  }

  return null;
};

export const getOptionDisabled = ({
  isSelected,
  isExclusiveOption,
  isMaxSelectReached,
  globalDisabled,
  hasSelectedExclusiveOption,
}: {
  isSelected: boolean;
  isExclusiveOption: boolean;
  isMaxSelectReached: boolean;
  globalDisabled: boolean;
  hasSelectedExclusiveOption: boolean;
}): boolean => {
  if (globalDisabled) return true;
  if (hasSelectedExclusiveOption && !isSelected) return true;
  return isMaxSelectReached && !isSelected && !isExclusiveOption;
};

export const isMultiSelectMinimumSatisfied = ({
  selectedOptionIds,
  input,
  minSelect,
  isRequired,
  hasSelectedExclusiveOption,
}: {
  selectedOptionIds: OptionId[];
  input?: string;
  minSelect?: number;
  isRequired: boolean;
  hasSelectedExclusiveOption?: boolean;
}): boolean => {
  if (hasSelectedExclusiveOption) return true;

  const effectiveMinSelect = minSelect ?? (isRequired ? 1 : 0);
  const effectiveSelectionCount =
    selectedOptionIds.length + ((input ?? "").trim().length > 0 ? 1 : 0);

  return effectiveSelectionCount >= effectiveMinSelect;
};

export const optionsWithIdsFromText = (
  text: string[],
  namespace?: string
): Option[] => {
  const numTextOccurences: Record<string, number> = {};
  return text.map((t) => {
    numTextOccurences[t] = (numTextOccurences[t] ?? 0) + 1;
    return {
      id: uuidv5(t + "---" + (namespace ?? ""), uuidv5.URL) as OptionId,
      text: t,
    };
  });
};

export const createNewOption = (
  text: string,
  options: {
    id?: OptionId;
    pinned?: boolean;
    exclusiveOption?: boolean;
    media?: OptionMedia;
  } = {}
) => {
  return {
    id: options.id ?? (uuidv4() as OptionId),
    text,
    pinned: options.pinned ?? false,
    exclusiveOption: options.exclusiveOption ?? false,
    media: options.media && {
      type: options.media.type,
      name: options.media.name,
      url: options.media.url,
      fit: options.media.fit,
    },
  };
};

export const createNewRow = (text: string): MatrixRow => {
  return { id: uuidv4() as OptionId, text };
};

export const getKnownErrorMessage = (
  error: Error,
  device: "camera" | "microphone",
  t: TFunction<"common", undefined>
) => {
  if (error.name === "NotReadableError" && error.message === "Device in use") {
    return {
      message: t(`not-readable-${device}-in-use`),
      id: "not-readable-device-in-use",
    };
  } else if (
    error.name === "NotFoundError" &&
    error.message === "Requested device not found"
  ) {
    return {
      message: t(`${device}-not-found`),
      id: "device-not-found",
    };
  }
  return { message: undefined, id: undefined };
};

export const isMobileDevice = () => {
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) || isListenLabsApp()
  );
};

export const getResponseDeviceType = (): DeviceType =>
  isMobileDevice() ? "mobile" : "desktop";

/** Matches the Listen Labs native app webview, not mobile browsers. */
export const isListenLabsAppUserAgent = (userAgent: string) =>
  /Listen Labs (iOS|Android)/i.test(userAgent);

/** Detects if running inside the Listen Labs native app */
export const isListenLabsApp = () => {
  if (typeof navigator === "undefined") return false;
  return isListenLabsAppUserAgent(navigator.userAgent);
};

export const isListenLabsIOSApp = () => {
  if (typeof navigator === "undefined") return false;
  return /Listen Labs iOS/i.test(navigator.userAgent);
};

/** True when the URL already points at a Listen embed-proxy host. */
export const isProxyUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  try {
    return isEmbedProxyHostname(new URL(url).hostname);
  } catch {
    return false;
  }
};

/**
 * Convert a URL to this stack's embed-proxy host.
 * Example (US): https://www.zillow.com/path → https://https-www-zillow-com.embed.listenlabs.ai/path
 */
export const convertToProxyUrl = (
  url: string | undefined
): string | undefined => {
  if (!url) return undefined;

  // If already a proxy URL, return as-is
  if (isProxyUrl(url)) return url;

  try {
    const urlObj = new URL(url);
    const protocol = urlObj.protocol.replace(":", "");
    const hostname = urlObj.hostname.replace(/\./g, "-");
    const subdomain = `${protocol}-${hostname}`;
    const pathAndQuery = urlObj.pathname + urlObj.search + urlObj.hash;
    const proxyHost = getEmbedProxyHost();

    return `https://${subdomain}.${proxyHost}${pathAndQuery}`;
  } catch {
    // If URL parsing fails, return undefined
    return undefined;
  }
};

const parseYouTubeTimestampToSeconds = (t: string): number | undefined => {
  if (/^\d+$/.test(t)) return Number(t);

  const match = t.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
  if (!match || match[0] === "") return undefined;

  const hours = match[1] ? Number(match[1]) : 0;
  const minutes = match[2] ? Number(match[2]) : 0;
  const seconds = match[3] ? Number(match[3]) : 0;
  const total = hours * 3600 + minutes * 60 + seconds;
  return total > 0 ? total : undefined;
};

const buildYouTubeEmbedUrl = (videoId: string, t: string | null): string => {
  const params = new URLSearchParams();
  if (t) {
    const startSeconds = parseYouTubeTimestampToSeconds(t);
    if (startSeconds !== undefined) params.set("start", String(startSeconds));
  }
  const query = params.toString();
  return `https://www.youtube.com/embed/${videoId}${query ? `?${query}` : ""}`;
};

const convertYouTubeToEmbed = (url: string): string | undefined => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.replace("www.", "");

    if (hostname === "youtube.com" || hostname === "m.youtube.com") {
      if (urlObj.pathname === "/watch") {
        const videoId = urlObj.searchParams.get("v");
        if (videoId) {
          return buildYouTubeEmbedUrl(videoId, urlObj.searchParams.get("t"));
        }
      }

      const shortsMatch = urlObj.pathname.match(/^\/shorts\/([a-zA-Z0-9_-]+)/);
      if (shortsMatch?.[1]) {
        return `https://www.youtube.com/embed/${shortsMatch[1]}`;
      }

      const liveMatch = urlObj.pathname.match(/^\/live\/([a-zA-Z0-9_-]+)/);
      if (liveMatch?.[1]) {
        return `https://www.youtube.com/embed/${liveMatch[1]}`;
      }

      if (urlObj.pathname.startsWith("/embed/")) return url;
    }

    if (hostname === "youtu.be") {
      const videoId = urlObj.pathname.slice(1);
      if (videoId) {
        return buildYouTubeEmbedUrl(videoId, urlObj.searchParams.get("t"));
      }
    }
  } catch {
    return undefined;
  }
  return undefined;
};

const convertVimeoToEmbed = (url: string): string | undefined => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.replace("www.", "");

    if (hostname === "vimeo.com") {
      const match = urlObj.pathname.match(/^\/(\d+)(?:\/([a-f0-9]+))?/);
      if (match?.[1]) {
        const hash = match[2] ? `?h=${match[2]}` : "";
        return `https://player.vimeo.com/video/${match[1]}${hash}`;
      }
    }

    if (hostname === "player.vimeo.com") return url;
  } catch {
    return undefined;
  }
  return undefined;
};

const convertLoomToEmbed = (url: string): string | undefined => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.replace("www.", "");

    if (hostname === "loom.com") {
      const match = urlObj.pathname.match(/^\/share\/([a-f0-9]+)/);
      if (match?.[1]) {
        return `https://www.loom.com/embed/${match[1]}`;
      }
      if (urlObj.pathname.startsWith("/embed/")) return url;
    }
  } catch {
    return undefined;
  }
  return undefined;
};

const convertGoogleSlidesToEmbed = (url: string): string | undefined => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;

    if (hostname === "docs.google.com") {
      const match = urlObj.pathname.match(
        /^\/presentation\/d\/([a-zA-Z0-9_-]+)/
      );
      if (match?.[1] && !urlObj.pathname.includes("/embed")) {
        return `https://docs.google.com/presentation/d/${match[1]}/embed?start=false&loop=false`;
      }
    }
  } catch {
    return undefined;
  }
  return undefined;
};

export const convertToEmbeddableUrl = (url: string): string | undefined => {
  return (
    convertYouTubeToEmbed(url) ??
    convertVimeoToEmbed(url) ??
    convertLoomToEmbed(url) ??
    convertGoogleSlidesToEmbed(url)
  );
};

export const getEmbedUrl = (embUrl: string | null | undefined) => {
  const url = normalizeEmbedDisplayUrl(embUrl);
  if (!url) return undefined;

  let resolved: string | undefined;

  // If already a proxy URL, return as-is (no transformation needed)
  if (isProxyUrl(url)) {
    resolved = url;
  } else {
    const converted = convertToEmbeddableUrl(url);
    if (converted) {
      resolved = converted;
    } else {
      // handle figma embeds
      // source: https://www.figma.com/developers/embed#overview
      const figma_regex =
        // eslint-disable-next-line no-useless-escape
        /https:\/\/[\w\.-]+\.?figma.com\/([\w-]+)\/([0-9a-zA-Z]{22,128})(?:\/.*)?$/;
      const prototype_regex = /(?:www|embed)\.figma\.com\/proto\/(.*)/;
      const design_regex =
        /figma\.com\/design\/([0-9a-zA-Z]{22,128})\/[^?]*(?:\?node-id=([^&]+))?/;

      const figma_match = url.match(figma_regex);
      const prototype_match = figma_match
        ? url.match(prototype_regex)?.[1]
        : null;

      // Check if it's a design file URL
      const design_match = url.match(design_regex);
      const fileKey = design_match?.[1];
      const nodeId = design_match?.[2];

      if (figma_match && prototype_match) {
        // It's already a prototype URL
        const embedOrigin = encodeURIComponent(getFigmaEmbedOrigin());
        resolved = `https://embed.figma.com/proto/${prototype_match}&embed-host=listenlabs&embed-origin=${embedOrigin}&scaling=fit&footer=false`;
      } else if (fileKey && nodeId) {
        // It's a design file URL - convert to embed prototype
        const embedOrigin = encodeURIComponent(getFigmaEmbedOrigin());
        resolved = `https://embed.figma.com/proto/${fileKey}?node-id=${nodeId}&embed-host=listenlabs&embed-origin=${embedOrigin}&scaling=fit&footer=false`;
      } else {
        // Fallback to normalized URL
        resolved = url;
      }
    }
  }

  return resolved && isValidUrl(resolved) ? resolved : undefined;
};

// Iframe loads URL here. Returns undefined to not render iframe on failure
export const getEmbedLoadUrl = (
  embed: EmbedDef | undefined
): string | undefined => {
  if (!embed || !("url" in embed)) return undefined;
  // Invalid proxy does not fail to url. Do not render iframe
  if (embed.proxyUrl?.trim()) {
    return normalizeEmbedProxyUrl(embed.proxyUrl);
  }
  return getEmbedUrl(embed.url);
};

// No allow-top-navigation / allow-modals: embeds must not redirect the interview or spoof dialogs.
const EMBED_IFRAME_SANDBOX =
  "allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation";

// Chromium refuses to render PDFs inside sandboxed iframes. Only survey-documents qualifies: its uploads are restricted to application/pdf, while survey-images has no such guarantee.
export const getEmbedIframeSandbox = (url: string): string | undefined =>
  parsePrivateSurveyAssetUrl(url)?.bucket === "survey-documents"
    ? undefined
    : EMBED_IFRAME_SANDBOX;

export const getRedirectSubmitTextOverride = ({
  item,
  config,
}: {
  item: { redirect_submit_text?: string };
  config: { redirect_submit_text_override?: string };
}): string | undefined =>
  item.redirect_submit_text ||
  config.redirect_submit_text_override ||
  undefined;
